import React, { useState } from 'react';
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { rangeOrder, ranges, summary, type RangeId } from '../../data/eau';
const numberFr = (value: number) => value.toLocaleString('fr-CA', {
  maximumFractionDigits: 1
});
export function ConsumptionCard() {
  const [rangeId, setRangeId] = useState<RangeId>('semaine');
  const range = ranges[rangeId];
  const down = summary.monthChange < 0;
  const Trend = down ? TrendingDownIcon : TrendingUpIcon;
  return <section aria-labelledby="consommation-titre" className="rounded-3xl border border-slate-200 bg-white p-6 lg:p-7" data-id="element-1160" data-mp="3l6l6h8p5cdhu">
      <h2 id="consommation-titre" className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-1161" data-mp="3q31tu0oiuntv">
        Consommation d’eau
      </h2>

      <dl className="mt-5 grid gap-5 sm:grid-cols-3" data-id="element-1162" data-mp="2epg70m64wybi">
        <div data-id="element-1163" data-mp="2eraplttxoecf">
          <dt className="text-[13px] font-bold text-slate-500" data-id="element-1164" data-mp="2enmz73jsc81x">Aujourd’hui</dt>
          <dd className="mt-1 text-4xl font-extrabold leading-none tracking-tight text-ville-navy" data-id="element-1165" data-mp="2eefdlxh8qx2p">
            {summary.todayLitres}
            <span className="ml-1 text-xl font-bold text-slate-400" data-id="element-1166" data-mp="2oc3aou2bkgo1">L</span>
          </dd>
          <dd className="mt-1 text-[13px] text-slate-500" data-id="element-1167" data-mp="2o9qjdqao8tyb">≈ {summary.todayCost}</dd>
        </div>
        <div data-id="element-1168" data-mp="2jf31iaa6axsp">
          <dt className="text-[13px] font-bold text-slate-500" data-id="element-1169" data-mp="2xcrnjlfz8azc">Ce mois-ci</dt>
          <dd className="mt-1 text-4xl font-extrabold leading-none tracking-tight text-ville-navy" data-id="element-1170" data-mp="2xaons9p0v3hf">
            {numberFr(summary.monthCubic)}
            <span className="ml-1 text-xl font-bold text-slate-400" data-id="element-1171" data-mp="3734a1j7cqe84">m³</span>
          </dd>
          <dd className={`mt-1 flex items-center gap-1 text-[13px] font-bold ${down ? 'text-ville-indigo' : 'text-ville-coral'}`} data-id="element-1172" data-mp="322zp9gzeuxc3">
            <Trend className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1173" data-mp="3c1nwrltrru1j" />
            {numberFr(Math.abs(summary.monthChange))} % vs le mois dernier
          </dd>
        </div>
        <div data-id="element-1174" data-mp="3j6p0vr40y6f1">
          <dt className="text-[13px] font-bold text-slate-500" data-id="element-1175" data-mp="3j2suh02giep4">Prévision fin de mois</dt>
          <dd className="mt-1 text-4xl font-extrabold leading-none tracking-tight text-ville-navy" data-id="element-1176" data-mp="3o48gaucgkp8k">
            {summary.forecastAmount}
          </dd>
          <dd className="mt-1 text-[13px] text-slate-500" data-id="element-1177" data-mp="1onnqkb687g5">{summary.forecastCubic}</dd>
        </div>
      </dl>

      <div role="tablist" aria-label="Période affichée" className="mt-6 flex gap-1 rounded-full bg-ville-mist p-1" data-id="element-1178" data-mp="6mfevc3zrpja">
        {rangeOrder.map(id => <button key={id} type="button" role="tab" aria-selected={id === rangeId} onClick={() => setRangeId(id)} className={`flex-1 rounded-full px-4 py-2 text-sm font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-blue ${id === rangeId ? 'bg-white text-ville-navy shadow-sm' : 'text-ville-deep/70 hover:text-ville-deep'}`} data-id="element-1179" data-mp="fnnepym5oyfh">
            {ranges[id].label}
          </button>)}
      </div>

      <div className="mt-5 h-[260px] w-full" data-id="element-1180" data-mp="255agu0vejbeh">
        <ResponsiveContainer width="100%" height="100%" data-id="element-1181" data-mp="2562ctcswvb5a">
          <BarChart data={range.points} margin={{
          top: 8,
          right: 8,
          bottom: 0,
          left: -12
        }} data-id="element-1182" data-mp="2067j71hr47gj">
            <CartesianGrid vertical={false} stroke="#e2e8f0" data-id="element-1183" data-mp="2xty585bg6gw9" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{
            fill: '#64748b',
            fontSize: 12
          }} data-id="element-1184" data-mp="2xtg4owhr5gij" />
            <YAxis tickLine={false} axisLine={false} width={48} tick={{
            fill: '#94a3b8',
            fontSize: 12
          }} tickFormatter={(value: number) => numberFr(Number(value))} data-id="element-1185" data-mp="2nuqqpesz8g7k" />
            <Tooltip cursor={{
            fill: '#e9eef7'
          }} formatter={value => [`${numberFr(Number(value))} ${range.unit}`, 'Consommation']} contentStyle={{
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            fontSize: 13,
            fontWeight: 600,
            color: '#0f2450'
          }} data-id="element-1186" data-mp="1ce8zbt48l3rs" />
            <ReferenceLine y={range.benchmark} stroke="#94a3b8" strokeDasharray="4 4" label={{
            value: 'Résidences semblables',
            position: 'insideTopRight',
            fill: '#64748b',
            fontSize: 11
          }} data-id="element-1187" data-mp="2mbp9t6t71pep" />
            <Bar dataKey="value" fill="#1d3f86" radius={[6, 6, 0, 0]} maxBarSize={38} data-id="element-1188" data-mp="354t2opyl7y89" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <p className="mt-3 text-[13px] text-slate-500" data-id="element-1189" data-mp="3a57codjr1i8x">
        Valeurs en {range.unit}. Le compteur intelligent transmet une lecture par heure.
      </p>
    </section>;
}